import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Ship, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

const RegisterPage = () => {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("token");

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!invitationToken);
  const [invitation, setInvitation] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [formData, setFormData] = useState({ name: "", password: "" });

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!invitationToken) {
      setValidating(false);
      setTokenError("Se requiere un enlace de invitación para registrarse. Contacta a tu administrador.");
      return;
    }
    axios.get(`${API}/auth/invitation/${invitationToken}`)
      .then((res) => {
        setInvitation(res.data);
        setValidating(false);
      })
      .catch((err) => {
        setTokenError(err.response?.data?.detail || "Invitación inválida o expirada");
        setValidating(false);
      });
  }, [invitationToken]);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invitationToken) return;
    setLoading(true);
    try {
      await register({
        name: formData.name,
        password: formData.password,
        invitation_token: invitationToken,
      });
      toast.success("Cuenta creada exitosamente");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrarse");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_compliance-vessel/artifacts/1ghcrk1z_GREEN%20BUNKERS%20Horizontal.png" 
              alt="Green Bunkers" 
              className="h-16 w-auto"
            />
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-2xl font-serif text-[#0b3e65]">Crear Cuenta</CardTitle>
              <CardDescription>
                Registro por invitación
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {validating ? (
                <div className="flex items-center gap-3 text-slate-600 py-8">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Validando invitación...</span>
                </div>
              ) : tokenError ? (
                <div className="flex flex-col items-center gap-4 py-8 text-center" data-testid="register-error">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                  <p className="text-red-600 font-medium">{tokenError}</p>
                  <Link to="/login" className="text-[#f37022] hover:underline text-sm">
                    Ir a Iniciar Sesión
                  </Link>
                </div>
              ) : invitation ? (
                <>
                  {/* Invitation info */}
                  <div className="bg-[#0b3e65]/5 border border-[#0b3e65]/10 rounded-lg p-4 mb-6" data-testid="invitation-info">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-[#0b3e65]">Invitación válida</span>
                    </div>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><span className="font-medium">Email:</span> {invitation.email}</p>
                      <p><span className="font-medium">Empresa:</span> {invitation.company}</p>
                      <p><span className="font-medium">Rol:</span> {invitation.role === "admin" ? "Administrador" : "Usuario"}</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre completo</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Tu nombre"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="rounded-sm"
                        data-testid="register-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Contraseña</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Crea una contraseña segura"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                        className="rounded-sm"
                        data-testid="register-password"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm"
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creando cuenta...
                        </>
                      ) : (
                        "Crear Cuenta"
                      )}
                    </Button>
                  </form>
                </>
              ) : null}

              <p className="text-center text-sm text-slate-600 mt-6">
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" className="text-[#f37022] hover:underline font-medium">
                  Iniciar Sesión
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1717603781253-7d3951e2c45e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-[#0b3e65]/70 flex flex-col items-center justify-center text-white p-12">
          <Ship className="w-20 h-20 mb-6 text-[#f37022]" />
          <h2 className="text-3xl font-serif text-center mb-4">Vessel Screening</h2>
          <p className="text-lg text-center text-slate-200 max-w-md">
            Screening en tiempo real contra OFAC SDN, Consejo de Seguridad ONU, y listas consolidadas de la UE
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
